<template>
  <div class="custom-video-player" :class="{ 'dark-theme': isDarkTheme }" :style="playerStyles">
    <div class="video-wrapper">
      <video
        ref="videoElement"
        :src="src"
        :poster="poster"
        class="video-element"
        :class="{ 'video-element--audio': isAudioOnly }"
        playsinline
        webkit-playsinline
        @timeupdate="updateProgress"
        @loadedmetadata="onLoadedMetadata"
        @ended="onEnded"
        @play="isPlaying = true"
        @pause="isPlaying = false"
        disablePictureInPicture
        controlsList="nodownload"
      ></video>

      <!-- Spectrum de áudio quando não há poster -->
      <div v-if="!poster && isAudioOnly" class="spectrum-overlay-wrapper">
        <AudioSpectrum :isPlaying="isPlaying" :primaryColor="computedPrimaryColor" />
      </div>

      <!-- Overlay de controles -->
      <div class="video-overlay" @click="togglePlay">
        <transition name="fade">
          <div v-if="showPlayIcon" class="play-icon-overlay">
            <i class="material-icons">{{ isPlaying ? 'pause' : 'play_arrow' }}</i>
          </div>
        </transition>
      </div>
    </div>

    <!-- Controles personalizados -->
    <div class="controls-container">
      <!-- Barra de progresso -->
      <div class="progress-bar-wrapper" @click="seekTo">
        <div class="progress-bar">
          <div class="progress-filled" :style="{ width: progressPercent + '%' }"></div>
          <div class="progress-handle" :style="{ left: progressPercent + '%' }"></div>
        </div>
      </div>

      <!-- Controles inferiores -->
      <div class="controls-bottom">
        <div class="controls-left">
          <!-- Botão Play/Pause -->
          <button class="control-btn" @click="togglePlay" :title="isPlaying ? 'Pausar' : 'Reproduzir'">
            <i class="material-icons">{{ isPlaying ? 'pause' : 'play_arrow' }}</i>
          </button>

          <!-- Botão Stop -->
          <button class="control-btn" @click="stop" title="Parar">
            <i class="material-icons">stop</i>
          </button>

          <!-- Tempo -->
          <div class="time-display">
            <span class="current-time">{{ formatTime(currentTime) }}</span>
            <span class="time-separator">/</span>
            <span class="duration">{{ formatTime(duration) }}</span>
          </div>
        </div>

        <div class="controls-right">
          <!-- Controle de volume -->
          <div class="volume-control">
            <button class="control-btn" @click="toggleMute" :title="isMuted ? 'Ativar som' : 'Silenciar'">
              <i class="material-icons">{{ volumeIcon }}</i>
            </button>
            <div class="volume-slider" @mouseenter="showVolumeSlider = true" @mouseleave="showVolumeSlider = false">
              <transition name="slide-up">
                <div v-show="showVolumeSlider" class="volume-bar-wrapper">
                  <input
                    type="range"
                    class="volume-bar"
                    min="0"
                    max="100"
                    v-model="volume"
                    @input="changeVolume"
                    orient="vertical"
                  />
                </div>
              </transition>
            </div>
          </div>

          <!-- Botão de velocidade -->
          <div class="speed-control">
            <button class="control-btn speed-btn" @click="toggleSpeedMenu" title="Velocidade de reprodução">
              <span class="speed-text">{{ playbackRate }}x</span>
            </button>
            <transition name="slide-up">
              <div v-show="showSpeedMenu" class="speed-menu">
                <button
                  v-for="rate in playbackRates"
                  :key="rate"
                  class="speed-option"
                  :class="{ active: playbackRate === rate }"
                  @click="changeSpeed(rate)"
                >
                  {{ rate }}x
                </button>
              </div>
            </transition>
          </div>

          <!-- Botão fullscreen (apenas para vídeo). Escondido no app iOS:
               com isElementFullscreenEnabled desligado no shell (ver
               BridgeViewController.swift), requestFullscreen de elemento não
               existe e a cadeia de fallbacks inteira vira undefined — o botão
               ficaria mudo. Antes da flag ele "funcionava" corrompendo o
               viewport do WKWebView na saída (capacitor#8054). -->
          <button
            v-if="!isAudioOnly && !$isIos"
            class="control-btn"
            @click="toggleFullscreen"
            :title="isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'"
          >
            <i class="material-icons">{{ isFullscreen ? 'fullscreen_exit' : 'fullscreen' }}</i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AudioSpectrum from './AudioSpectrum.vue';

export default {
  name: 'CustomVideoPlayer',
  components: {
    AudioSpectrum,
  },
  props: {
    src: {
      type: String,
      required: true,
    },
    poster: {
      type: String,
      default: '',
    },
    autoplay: {
      type: Boolean,
      default: false,
    },
    isAudioOnly: {
      type: Boolean,
      default: false,
    },
    primaryColor: {
      type: String,
      default: null,
    },
    // Posição inicial (segundos) para resume de mídia baixada. 0 = começa do início.
    startAt: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      volume: 100,
      isMuted: false,
      isFullscreen: false,
      showVolumeSlider: false,
      showSpeedMenu: false,
      showPlayIcon: false,
      playbackRate: 1,
      playbackRates: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2],
      playIconTimeout: null,
      hasEmittedComplete: false,
      /** Um seek externo ja posicionou a midia; o resume nao deve sobrescrever. */
      seekSolicitado: false,
    };
  },
  computed: {
    progressPercent() {
      if (!this.duration) return 0;
      return (this.currentTime / this.duration) * 100;
    },
    volumeIcon() {
      if (this.isMuted || this.volume === 0) return 'volume_off';
      if (this.volume < 50) return 'volume_down';
      return 'volume_up';
    },
    isDarkTheme() {
      return this.$theme?.darkTheme || false;
    },
    computedPrimaryColor() {
      return this.primaryColor || this.$theme?.primaryColor || '#ff6600';
    },
    playerStyles() {
      return {
        '--primary-color': this.computedPrimaryColor,
      };
    },
  },
  mounted() {
    if (this.autoplay) {
      this.$nextTick(() => {
        this.play();
      });
    }
    // Adicionar listener para fullscreen
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', this.handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', this.handleFullscreenChange);
  },
  beforeDestroy() {
    // Remover listeners
    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('mozfullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('MSFullscreenChange', this.handleFullscreenChange);
  },
  methods: {
    togglePlay() {
      const video = this.$refs.videoElement;
      if (video.paused) {
        this.play();
      } else {
        this.pause();
      }
    },
    play() {
      const video = this.$refs.videoElement;
      if (!video) return;

      // Story-2499: o play passou a ser chamado de fora (clique na transcricao, deep-link
      // com hash de tempo), quando o elemento pode ainda nao existir. O catch cobre a
      // recusa de autoplay do navegador, que devolve promise rejeitada e sujaria o console.
      const promessa = video.play();
      if (promessa && typeof promessa.catch === 'function') promessa.catch(() => {});

      this.isPlaying = true;
      this.$emit('play');
    },
    pause() {
      const video = this.$refs.videoElement;
      video.pause();
      this.isPlaying = false;
      this.$emit('pause');
    },
    stop() {
      const video = this.$refs.videoElement;
      video.pause();
      video.currentTime = 0;
      this.isPlaying = false;
      this.hasEmittedComplete = false; // Reset quando parar
      this.$emit('stop');
    },
    updateProgress() {
      const video = this.$refs.videoElement;
      this.currentTime = video.currentTime;
      
      // Emitir evento de progresso com a porcentagem atual
      const progress = this.progressPercent;
      this.$emit('progress', progress);
      this.$emit('time', this.currentTime);
      
      // Verificar se chegou a 100% (ou muito próximo, considerando precisão de ponto flutuante)
      if (progress >= 99.5 && !this.hasEmittedComplete) {
        this.hasEmittedComplete = true;
        this.$emit('complete', 100);
      }
    },
    seekTo(event) {
      const video = this.$refs.videoElement;
      const progressBar = event.currentTarget;
      const rect = progressBar.getBoundingClientRect();
      const pos = (event.clientX - rect.left) / rect.width;
      video.currentTime = pos * this.duration;
    },
    /**
     * Posiciona a midia num instante, em segundos.
     *
     * Story-2499: seek PROGRAMATICO, distinto do seekTo acima, que le a posicao do
     * clique na barra de progresso e por isso so serve a interacao direta com ela.
     * Este e chamado de fora — clique numa frase da transcricao, deep-link com hash
     * de tempo — casos em que o player de video usa window.vdplayer.seekTo, global
     * que so o bundle do coremedia expoe.
     *
     * @param {number} seconds instante desejado
     *
     * @example this.$refs.audioPlayer.seek(125.4)
     */
    seek(seconds) {
      const video = this.$refs.videoElement;
      if (!video || !Number.isFinite(seconds)) return;

      // Antes do loadedmetadata a duracao ainda e 0 (ou NaN, se o proprio elemento
      // devolver isso), e nao ha limite a aplicar — o navegador trata a atribuicao como
      // posicao inicial de reproducao.
      const limite = Number.isFinite(this.duration) && this.duration > 0 ? this.duration : null;
      video.currentTime = limite ? Math.min(Math.max(seconds, 0), limite) : Math.max(seconds, 0);

      this.currentTime = video.currentTime;

      // Um seek explicito vence o resume: sem esta marca, o onLoadedMetadata aplicaria
      // startAt logo depois e a posicao pedida seria descartada. Acontece de verdade em
      // aula baixada com posicao salva aberta por deep-link com hash de tempo — o aluno
      // clica num instante e cai onde tinha parado.
      this.seekSolicitado = true;
    },
    changeVolume() {
      const video = this.$refs.videoElement;
      video.volume = this.volume / 100;
      this.isMuted = this.volume === 0;
    },
    toggleMute() {
      const video = this.$refs.videoElement;
      if (this.isMuted) {
        video.volume = this.volume / 100;
        this.isMuted = false;
      } else {
        video.volume = 0;
        this.isMuted = true;
      }
    },
    toggleSpeedMenu() {
      this.showSpeedMenu = !this.showSpeedMenu;
    },
    changeSpeed(rate) {
      const video = this.$refs.videoElement;
      video.playbackRate = rate;
      this.playbackRate = rate;
      this.showSpeedMenu = false;
    },
    toggleFullscreen() {
      const playerElement = this.$el;
      
      if (!document.fullscreenElement && !document.webkitFullscreenElement && 
          !document.mozFullScreenElement && !document.msFullscreenElement) {
        if (playerElement.requestFullscreen) {
          playerElement.requestFullscreen();
        } else if (playerElement.webkitRequestFullscreen) {
          playerElement.webkitRequestFullscreen();
        } else if (playerElement.mozRequestFullScreen) {
          playerElement.mozRequestFullScreen();
        } else if (playerElement.msRequestFullscreen) {
          playerElement.msRequestFullscreen();
        }
      } else {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }
      }
    },
    handleFullscreenChange() {
      this.isFullscreen = !!(document.fullscreenElement || document.webkitFullscreenElement || 
                            document.mozFullScreenElement || document.msFullscreenElement);
    },
    onLoadedMetadata() {
      const video = this.$refs.videoElement;
      this.duration = video.duration;
      // Resume: posiciona no ponto salvo (uma vez, quando os metadados carregam).
      if (!this.seekSolicitado && this.startAt > 0 && this.startAt < this.duration) {
        video.currentTime = this.startAt;
      }
      this.$emit('loadedmetadata', { duration: this.duration });
    },
    onEnded() {
      this.isPlaying = false;
      // Garantir que emitimos o evento de complete ao finalizar
      if (!this.hasEmittedComplete) {
        this.hasEmittedComplete = true;
        this.$emit('complete', 100);
      }
      this.$emit('ended');
    },
    formatTime(seconds) {
      if (!seconds || isNaN(seconds)) return '0:00';
      const h = Math.floor(seconds / 3600);
      const m = Math.floor((seconds % 3600) / 60);
      const s = Math.floor(seconds % 60);
      
      if (h > 0) {
        return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
      }
      return `${m}:${s.toString().padStart(2, '0')}`;
    },
  },
};
</script>

<style scoped lang="scss">
.custom-video-player {
  width: 100%;
  background: #000;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  font-family: 'Roboto', sans-serif;

  &.dark-theme {
    background: #1a1a1a;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  }
}

.video-wrapper {
  position: relative;
  width: 100%;
  background: #000;
}

.spectrum-overlay-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  pointer-events: none;
}

.video-element {
  width: 100%;
  display: block;
  max-height: 70vh;
}

// Áudio: o <video> carrega só o mp3 (sem faixa de vídeo). No WebView mobile isso
// renderiza um placeholder cinza + play nativo por cima do AudioSpectrum. Esconde o
// viewport (mantém a altura do frame) e bloqueia toques nativos — o AudioSpectrum +
// os controles custom passam a ser a UI.
.video-element--audio {
  opacity: 0;
  pointer-events: none;
}

.video-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: transparent;
  transition: background 0.3s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.1);
  }
}

.play-icon-overlay {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;

  i {
    font-size: 48px;
    color: #fff;
  }
}

.controls-container {
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.9));
  padding: 12px 16px;
}

.progress-bar-wrapper {
  margin-bottom: 12px;
  cursor: pointer;
  padding: 8px 0;
}

.progress-bar {
  position: relative;
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  overflow: visible;
  transition: height 0.2s ease;

  &:hover {
    height: 6px;

    .progress-handle {
      opacity: 1;
      transform: translateX(-50%) scale(1);
    }
  }
}

.progress-filled {
  height: 100%;
  background: var(--primary-color, #ff6600);
  border-radius: 2px;
  transition: width 0.1s linear;
}

.progress-handle {
  position: absolute;
  top: 50%;
  transform: translateX(-50%) translateY(-50%) scale(0);
  width: 14px;
  height: 14px;
  background: var(--primary-color, #ff6600);
  border: 2px solid #fff;
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  opacity: 0;
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.controls-bottom {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.controls-left,
.controls-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.control-btn {
  background: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: var(--primary-color, #ff6600);
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }

  i {
    font-size: 24px;
  }
}

.time-display {
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
  user-select: none;
  min-width: 100px;

  .time-separator {
    opacity: 0.6;
  }
}

.volume-control {
  position: relative;
  display: flex;
  align-items: center;
}

.volume-slider {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;
}

.volume-bar-wrapper {
  background: rgba(0, 0, 0, 0.9);
  padding: 12px 8px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
}

.volume-bar {
  -webkit-appearance: slider-vertical;
  width: 4px;
  height: 80px;
  background: rgba(255, 255, 255, 0.3);
  outline: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 14px;
    height: 14px;
    background: #fff;
    cursor: pointer;
    border-radius: 50%;
  }

  &::-moz-range-thumb {
    width: 14px;
    height: 14px;
    background: #fff;
    cursor: pointer;
    border-radius: 50%;
    border: none;
  }
}

.speed-control {
  position: relative;
}

.speed-btn {
  min-width: 50px;
}

.speed-text {
  font-size: 14px;
  font-weight: 600;
}

.speed-menu {
  position: absolute;
  bottom: 100%;
  right: 0;
  margin-bottom: 8px;
  background: rgba(0, 0, 0, 0.95);
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  min-width: 80px;
}

.speed-option {
  width: 100%;
  padding: 10px 16px;
  background: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  font-size: 14px;
  text-align: left;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  &.active {
    background: rgba(255, 102, 0, 0.2);
    color: var(--primary-color, #ff6600);
    font-weight: 600;
  }
}

// Transições
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition: all 0.2s ease;
}

.slide-up-enter,
.slide-up-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

// Responsividade
@media (max-width: 768px) {
  .controls-container {
    padding: 8px 12px;
  }

  .control-btn {
    width: 36px;
    height: 36px;
    padding: 6px;

    i {
      font-size: 20px;
    }
  }

  .time-display {
    font-size: 12px;
    min-width: 80px;
  }

  .speed-text {
    font-size: 12px;
  }

  .play-icon-overlay {
    width: 60px;
    height: 60px;

    i {
      font-size: 36px;
    }
  }
}

// Fullscreen styles
.custom-video-player:fullscreen,
.custom-video-player:-webkit-full-screen,
.custom-video-player:-moz-full-screen,
.custom-video-player:-ms-fullscreen {
  .video-element {
    max-height: 100vh;
  }
}
</style>

